// Admin UI for B2B email templates: list 160 slots, edit overrides, Claude-translate.
// Lightweight modal-based editor — fancier WYSIWYG can come later.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Save, RotateCcw, Languages, ChevronLeft, ToggleLeft, ToggleRight } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const CAT_LABELS = {
  domaines_viticoles: "Domaines viticoles", cavistes: "Cavistes", negociants: "Négociants",
  restaurateurs: "Restaurateurs", distributeurs: "Distributeurs", importateurs: "Importateurs",
  ecoles_du_vin: "Écoles du vin", drinks_tech: "Drinks Tech", ecommerce: "E-commerce", autre: "Autre",
};
const STEP_LABEL = { 1: "J0", 2: "J+5", 3: "J+10", 4: "J+20" };
const LANG_FLAG = { fr: "🇫🇷", en: "🇬🇧", es: "🇪🇸", pt: "🇵🇹" };

function Editor({ category, lang, step, onClose, refreshList }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [translating, setTranslating] = useState(false);
  /* eslint-disable */
  useEffect(() => {
    (async () => {
      setBusy(true);
      try { setData(await adminApi.getEmailTemplate(category, lang, step)); }
      finally { setBusy(false); }
    })();
  }, [category, lang, step]);
  /* eslint-enable */

  const setUC = (k, v) => setData((d) => ({ ...d, use_case: { ...d.use_case, [k]: v } }));
  const setSt = (k, v) => setData((d) => ({ ...d, step_template: { ...d.step_template, [k]: v } }));

  const saveStep = async () => {
    setBusy(true);
    try {
      await adminApi.patchEmailTemplateStep(category, lang, step, data.step_template);
      const r = await adminApi.getEmailTemplate(category, lang, step);
      setData(r); refreshList();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const saveUC = async () => {
    setBusy(true);
    try {
      await adminApi.patchEmailTemplateUseCase(category, lang, data.use_case);
      const r = await adminApi.getEmailTemplate(category, lang, step);
      setData(r); refreshList();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const resetStep = async () => {
    if (!window.confirm("Restaurer le template d'origine pour ce step + lang ?")) return;
    setBusy(true);
    try { await adminApi.resetEmailTemplateStep(category, lang, step); }
    catch (e) { alert(e.message); }
    finally {
      setBusy(false);
      const r = await adminApi.getEmailTemplate(category, lang, step);
      setData(r); refreshList();
    }
  };
  const translateUC = async () => {
    if (!window.confirm("Traduire ce use_case depuis FR vers EN/ES/PT via Claude ?")) return;
    setTranslating(true);
    try {
      const r = await adminApi.translateEmailTemplate({
        source_lang: "fr", target_langs: ["en", "es", "pt"],
        category, save: true,
      });
      const langs = Object.entries(r.results).filter(([, x]) => x.ok).map(([l]) => l).join(", ");
      alert(`✓ Traduit et sauvegardé : ${langs || "rien"}`);
      const ref = await adminApi.getEmailTemplate(category, lang, step); setData(ref); refreshList();
    } catch (e) { alert(e.message); } finally { setTranslating(false); }
  };

  if (!data) return <div className="p-8 text-sm text-[color:var(--gv-ink)]/60" data-testid="editor-loading"><Loader2 className="inline animate-spin mr-2" size={14}/> Chargement…</div>;

  return (
    <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5" data-testid="template-editor-pane">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <button onClick={onClose} className="text-xs inline-flex items-center gap-1 text-[color:var(--gv-ink)]/70 hover:text-[color:var(--gv-pink)]" data-testid="editor-back"><ChevronLeft size={14}/> Retour à la grille</button>
        <div className="text-sm font-semibold">{LANG_FLAG[lang]} {CAT_LABELS[category] || category} · {STEP_LABEL[step]}</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Step template */}
        <div>
          <h4 className="text-xs uppercase tracking-wide text-[color:var(--gv-ink)]/60 mb-2 font-semibold">Step template (sujet, intro, paragraphes)</h4>
          {["subject", "preheader", "title", "intro"].map((k) => (
            <label key={k} className="block mb-2">
              <span className="text-[11px] text-[color:var(--gv-ink)]/60">{k}</span>
              <input value={data.step_template[k] || ""} onChange={(e) => setSt(k, e.target.value)} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-1.5 text-sm" data-testid={`step-${k}`} />
            </label>
          ))}
          <label className="block mb-2">
            <span className="text-[11px] text-[color:var(--gv-ink)]/60">paragraphs (un par ligne)</span>
            <textarea rows={6} value={(data.step_template.paragraphs || []).join("\n")} onChange={(e) => setSt("paragraphs", e.target.value.split("\n").filter(Boolean))} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-1.5 text-sm font-mono" data-testid="step-paragraphs" />
          </label>
          {["cta_primary", "cta_secondary"].map((k) => (
            <label key={k} className="block mb-2">
              <span className="text-[11px] text-[color:var(--gv-ink)]/60">{k}</span>
              <input value={data.step_template[k] || ""} onChange={(e) => setSt(k, e.target.value)} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-1.5 text-sm" data-testid={`step-${k}`} />
            </label>
          ))}
          <div className="flex gap-2 mt-3">
            <button onClick={saveStep} disabled={busy} className="btn-primary text-sm disabled:opacity-50" data-testid="save-step"><Save size={14}/> Sauvegarder step</button>
            <button onClick={resetStep} disabled={busy} className="btn-secondary text-sm disabled:opacity-50" data-testid="reset-step"><RotateCcw size={14}/> Reset</button>
          </div>
        </div>

        {/* Use case + composed preview */}
        <div>
          <h4 className="text-xs uppercase tracking-wide text-[color:var(--gv-ink)]/60 mb-2 font-semibold">Use case (catégorie {CAT_LABELS[category]})</h4>
          {["noun_plural", "pain", "use_case", "peers"].map((k) => (
            <label key={k} className="block mb-2">
              <span className="text-[11px] text-[color:var(--gv-ink)]/60">{k}</span>
              {k === "use_case" ? (
                <textarea rows={3} value={data.use_case[k] || ""} onChange={(e) => setUC(k, e.target.value)} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-1.5 text-sm" data-testid={`uc-${k}`} />
              ) : (
                <input value={data.use_case[k] || ""} onChange={(e) => setUC(k, e.target.value)} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-1.5 text-sm" data-testid={`uc-${k}`} />
              )}
            </label>
          ))}
          <label className="block mb-2">
            <span className="text-[11px] text-[color:var(--gv-ink)]/60">examples (un par ligne)</span>
            <textarea rows={4} value={(data.use_case.examples || []).join("\n")} onChange={(e) => setUC("examples", e.target.value.split("\n").filter(Boolean))} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-1.5 text-sm" data-testid="uc-examples" />
          </label>
          <div className="flex gap-2 mt-3 flex-wrap">
            <button onClick={saveUC} disabled={busy} className="btn-primary text-sm disabled:opacity-50" data-testid="save-uc"><Save size={14}/> Sauvegarder use_case</button>
            {lang === "fr" && (
              <button onClick={translateUC} disabled={translating} className="btn-secondary text-sm disabled:opacity-50" data-testid="translate-uc">
                {translating ? <Loader2 className="animate-spin" size={14}/> : <Languages size={14}/>} Traduire FR → EN/ES/PT
              </button>
            )}
          </div>

          {data.composed_preview && (
            <div className="mt-5 p-3 bg-[color:var(--gv-mist)]/40 border border-[color:var(--gv-line)] rounded-[3px]" data-testid="composed-preview">
              <div className="text-[11px] uppercase tracking-wide text-[color:var(--gv-ink)]/60 mb-1">Aperçu composé</div>
              <div className="text-[13px] font-semibold mb-1">{data.composed_preview.subject}</div>
              <div className="text-[12px] italic text-[color:var(--gv-ink)]/60 mb-2">{data.composed_preview.preheader}</div>
              <div className="text-[12px]" dangerouslySetInnerHTML={{ __html: (data.composed_preview.paragraphs || []).join("<br/>") }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const AdminEmailTemplatesPage = () => {
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try { setList(await adminApi.listEmailTemplates()); } finally { setLoading(false); }
  };
  /* eslint-disable */
  useEffect(() => { refresh(); }, []);
  /* eslint-enable */

  const togglePersonalize = async (cat) => {
    const curr = !!list.personalize_flags?.[cat];
    await adminApi.setEmailPersonalize(cat, !curr);
    setList((l) => ({ ...l, personalize_flags: { ...l.personalize_flags, [cat]: !curr } }));
  };

  if (loading || !list) return <div className="p-10 text-sm text-[color:var(--gv-ink)]/60" data-testid="templates-loading"><Loader2 className="inline animate-spin mr-2" size={14}/> Chargement…</div>;

  return (
    <div className="space-y-6" data-testid="admin-email-templates">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)] mb-1">Templates emails B2B</h1>
          <p className="text-sm text-[color:var(--gv-ink)]/60">{list.total} slots · {list.overridden} overrides DB · clique sur un slot pour éditer · 🌐 Traduction Claude · ⚡ Personnalisation live</p>
        </div>
        <Link to="/admin/email" className="btn-secondary text-sm" data-testid="back-to-overview">← Overview séquences</Link>
      </div>

      {selected ? (
        <Editor {...selected} onClose={() => setSelected(null)} refreshList={refresh} />
      ) : (
        <>
          {/* Personalize flags */}
          <section className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5" data-testid="personalize-section">
            <h3 className="font-display text-base mb-1">⚡ Personnalisation live via Claude (option D)</h3>
            <p className="text-xs text-[color:var(--gv-ink)]/60 mb-3">Pour chaque catégorie activée, Claude réécrit les paragraphes au moment de l&apos;envoi avec le first_name / company / role / country du contact.</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {list.categories.map((c) => {
                const enabled = !!list.personalize_flags?.[c];
                return (
                  <button key={c} onClick={() => togglePersonalize(c)} className={`flex items-center justify-between gap-2 px-3 py-2 rounded-[4px] border text-[12px] transition ${enabled ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-[color:var(--gv-line)] text-[color:var(--gv-ink)]/70 hover:bg-[color:var(--gv-mist)]"}`} data-testid={`personalize-${c}`}>
                    <span>{CAT_LABELS[c] || c}</span>
                    {enabled ? <ToggleRight size={18} className="text-emerald-600"/> : <ToggleLeft size={18} className="text-[color:var(--gv-ink)]/40"/>}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Grid 10 categories × 4 langs × 4 steps = 160 slots */}
          <section className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5" data-testid="templates-grid">
            <h3 className="font-display text-base mb-3">Grille des 160 templates</h3>
            <div className="space-y-3">
              {list.categories.map((cat) => (
                <details key={cat} className="border border-[color:var(--gv-line)] rounded-[3px]" data-testid={`cat-${cat}`}>
                  <summary className="cursor-pointer px-3 py-2 text-sm font-semibold bg-[color:var(--gv-mist)]/30 hover:bg-[color:var(--gv-mist)]/60 select-none">{CAT_LABELS[cat] || cat}</summary>
                  <div className="overflow-x-auto p-3">
                    <table className="w-full text-[12px]">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wide text-[color:var(--gv-ink)]/60">
                          <th className="py-1.5 pr-2 w-12">Lang</th>
                          {[1,2,3,4].map((s) => <th key={s} className="py-1.5 pr-2">{STEP_LABEL[s]}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {list.langs.map((lang) => (
                          <tr key={lang} className="border-t border-[color:var(--gv-line)]/40">
                            <td className="py-1.5 pr-2 font-semibold">{LANG_FLAG[lang]} {lang.toUpperCase()}</td>
                            {[1,2,3,4].map((step) => {
                              const row = list.items.find((it) => it.category === cat && it.lang === lang && it.step === step);
                              const overridden = row?.overridden_step || row?.overridden_use_case;
                              return (
                                <td key={step} className="py-1.5 pr-2">
                                  <button
                                    onClick={() => setSelected({ category: cat, lang, step })}
                                    className={`block w-full text-left px-2 py-1 rounded-[2px] border text-[11px] truncate hover:bg-[color:var(--gv-pink)]/5 transition ${overridden ? "border-amber-300 bg-amber-50" : "border-[color:var(--gv-line)] bg-white"}`}
                                    title={row?.subject || ""}
                                    data-testid={`slot-${cat}-${lang}-${step}`}
                                  >
                                    {overridden && <span className="text-amber-600 mr-1">●</span>}
                                    {(row?.subject || "").replace(/&nbsp;/g, " ").slice(0, 38)}
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              ))}
            </div>
            <div className="mt-3 text-[11px] text-[color:var(--gv-ink)]/50 flex items-center gap-3">
              <span><span className="text-amber-600">●</span> = override DB enregistré</span>
              <span>Clique sur un slot pour éditer + traduire</span>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default AdminEmailTemplatesPage;
